import {
  SmartDocEditorDescription_EditorType,
  type OutputDescription,
  type OutputReference,
  type ValueConfig,
} from "@smartsuite/automation-engine-api";
import type { ContentItem, Doc, EditorState } from "@smartsuite/smartdoc";
import { t } from "i18next";
import type {
  ActionCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../../../../utils/conditions-coverter.hook";
import { isSupportedOutput } from "../../../../utils";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";
import { isCurrentItemPresent } from "../../../../../../../../stores/automation/utils/inputValidation";
import { updateNestedSmartDocInLinkMarks } from "../../../../../../../../utils/smartdoc-nested-link-href";

export const EMPTY_SMARTDOC: Doc = {
  type: "doc",
  content: [
    {
      type: "paragraph",
      attrs: {
        textAlign: null,
      },
    },
  ],
};

export const parseSmartDoc = (smartDoc: string): Doc => {
  try {
    return JSON.parse(smartDoc);
  } catch {
    // Empty SmartDoc
    return {
      type: "doc",
      content: [
        {
          type: "paragraph",
          attrs: {
            textAlign: null,
          },
        },
      ],
    };
  }
};

export const renameRemovedPills = (
  smartdoc: Doc,
  trigger: TriggerCandidate,
  actions: ActionCandidate[],
  action?: ActionCandidate,
  looping?: LoopScope,
): {
  doc: Doc;
  hasInvalidPills: boolean;
} => {
  let hasInvalidPills = false;
  const triggerOutputs = getOutputDescriptions(trigger);

  const checkPillContent = (
    content: ContentItem,
  ): { contentItem: ContentItem; hasInvalidPills: boolean } => {
    let invalidPills = false;

    if (content.type === "list_item") {
      content.content = (content.content ?? []).map((c: ContentItem) => {
        if (
          c.content !== undefined &&
          Array.isArray(c.content) &&
          c.content.length > 0
        ) {
          c.content = c.content.map((c2: ContentItem) => {
            const { contentItem, hasInvalidPills } = checkPillContent(c2);
            invalidPills = hasInvalidPills;
            return contentItem;
          });
        }
        return c;
      });
      return { contentItem: content, hasInvalidPills: invalidPills };
    }
    if (content.type === "pill") {
      try {
        const value: OutputReference = JSON.parse(String(content.attrs?.value));
        if (value.source.oneofKind === "triggerReference") {
          // `looping` is the action's own loop scope; when undefined (action
          // outside any loop) isCurrentItemPresent falls back to the root
          // singleton, preserving the pre-scoping behavior.
          const currentItemPresent = isCurrentItemPresent(
            value.outputId,
            looping,
          );

          if (currentItemPresent) {
            return {
              contentItem: {
                ...content,
                attrs: {
                  ...content.attrs,
                  ...(typeof currentItemPresent === "object"
                    ? { title: currentItemPresent.label }
                    : {}),
                  invalid: false,
                },
              },
              hasInvalidPills: invalidPills,
            };
          }

          const isPresent = !!triggerOutputs.find(
            (output: OutputDescription) =>
              output.outputId === value.outputId &&
              value.source.oneofKind === "triggerReference" &&
              value.source.triggerReference.triggerId ===
                trigger.triggerReference?.triggerId,
          );
          if (!isPresent) {
            invalidPills = true;
          }

          return isPresent
            ? { contentItem: content, hasInvalidPills: invalidPills }
            : {
                contentItem: {
                  ...content,
                  attrs: {
                    ...content.attrs,
                    title: String(t("automation-form.deleted-field-title")),
                    invalid: true,
                  },
                },
                hasInvalidPills: invalidPills,
              };
        }
        if (
          value.source.oneofKind === "actionReference" &&
          actions.length > 0
        ) {
          const refAction = actions.find(
            (action: ActionCandidate) =>
              value.source.oneofKind === "actionReference" &&
              action.actionReference?.actionId ===
                value.source.actionReference.actionId &&
              action.actionReference?.instanceId ===
                value.source.actionReference.instanceId,
          );

          if (refAction === undefined) {
            return { contentItem: content, hasInvalidPills: invalidPills };
          }

          if (!isSupportedOutput(refAction, actions, action, looping)) {
            return {
              contentItem: {
                ...content,
                attrs: {
                  ...content.attrs,
                  title: String(t("automation-form.deleted-field-title")),
                  invalid: true,
                },
              },
              hasInvalidPills: invalidPills,
            };
          }

          const output = getOutputDescriptions(refAction);

          const currentItemPresent = isCurrentItemPresent(
            value.outputId,
            looping,
          );

          const isPresent = !!output.find(
            (output: OutputDescription) => output.outputId === value.outputId,
          );

          if (!isPresent && !currentItemPresent) {
            invalidPills = true;
          }

          if (currentItemPresent) {
            return {
              contentItem: {
                ...content,
                attrs: {
                  ...content.attrs,
                  ...(typeof currentItemPresent === "object"
                    ? { title: currentItemPresent.label }
                    : {}),
                  invalid: false,
                },
              },
              hasInvalidPills: invalidPills,
            };
          }
          return isPresent
            ? { contentItem: content, hasInvalidPills: invalidPills }
            : {
                contentItem: {
                  ...content,
                  attrs: {
                    ...content.attrs,
                    title: String(t("automation-form.deleted-field-title")),
                    invalid: true,
                  },
                },
                hasInvalidPills: invalidPills,
              };
        }
      } catch (_) {
        return { contentItem: content, hasInvalidPills: invalidPills };
      }
    }

    if (content.marks !== undefined && content.marks.length > 0) {
      const visitNode = (node: ContentItem): ContentItem => {
        const { contentItem, hasInvalidPills: nodeInvalid } =
          checkPillContent(node);
        if (nodeInvalid) invalidPills = true;
        return contentItem;
      };
      return {
        contentItem: updateNestedSmartDocInLinkMarks(content, visitNode),
        hasInvalidPills: invalidPills,
      };
    }

    return { contentItem: content, hasInvalidPills: invalidPills };
  };

  const contentItems = smartdoc.content.map((content: ContentItem) => {
    const innerContent = (content.content ?? []).map((c: ContentItem) => {
      const { contentItem, hasInvalidPills: countOfInvalid } =
        checkPillContent(c);
      hasInvalidPills = countOfInvalid;
      return contentItem;
    });

    return {
      ...content,
      content: innerContent,
    };
  });

  return {
    doc: {
      ...smartdoc,
      content: contentItems,
    },
    hasInvalidPills,
  };
};

export const toValueConfig = (
  value: EditorState,
  type?: SmartDocEditorDescription_EditorType,
): ValueConfig => {
  const json = value.toJSON();
  const doc = json.doc as Doc;
  if (type === SmartDocEditorDescription_EditorType.PILLS_SUMMARY) {
    return {
      values: [
        {
          value: {
            oneofKind: "smartdocPillsSummary",
            smartdocPillsSummary: JSON.stringify(doc),
          },
        },
      ],
    };
  }
  return {
    values: [
      {
        value: {
          oneofKind: "smartdoc",
          smartdoc: JSON.stringify(doc),
        },
      },
    ],
  };
};
